import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Plane,
  Bell,
  Settings,
  Download,
  LogOut,
  Pickaxe,
  LineChart,
  Cpu,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const nav = [
  { to: "/", label: "Rigs", Icon: LayoutDashboard, end: true, testid: "nav-rigs" },
  { to: "/flight-sheets", label: "Flight Sheets", Icon: Plane, testid: "nav-flight-sheets" },
  { to: "/oc-profiles", label: "OC Profiles", Icon: Cpu, testid: "nav-oc-profiles" },
  { to: "/metrics", label: "Metrics", Icon: LineChart, testid: "nav-metrics" },
  { to: "/activity", label: "Activity", Icon: Bell, testid: "nav-activity" },
  { to: "/agent", label: "Agent Install", Icon: Download, testid: "nav-agent" },
  { to: "/settings", label: "Settings", Icon: Settings, testid: "nav-settings" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-[#09090B] text-[#FAFAFA]">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-60 flex-col border-r border-[#27272A] bg-[#0B0B0F] sticky top-0 h-screen">
        <div className="px-5 py-5 border-b border-[#27272A] flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-sm bg-[#10B981] flex items-center justify-center">
            <Pickaxe className="w-4 h-4 text-[#09090B]" strokeWidth={2.5} />
          </div>
          <div className="leading-none">
            <div className="font-heading font-black tracking-tight text-lg">
              WART<span className="text-[#10B981]">HIVE</span>
            </div>
            <div className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.18em] mt-0.5 uppercase">
              v2.0 · mining ops
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 flex flex-col gap-1">
          {nav.map(({ to, label, Icon, end, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-body transition-colors ${
                  isActive
                    ? "bg-[#10B981]/10 text-[#10B981] border-l-2 border-[#10B981] pl-[10px]"
                    : "text-[#A1A1AA] hover:text-[#FAFAFA] hover:bg-[#18181B]"
                }`
              }
            >
              <Icon className="w-4 h-4" strokeWidth={2} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="px-3 py-4 border-t border-[#27272A]">
          <div className="px-3 py-2 mb-2">
            <div className="font-mono text-[10px] tracking-[0.2em] text-[#A1A1AA] uppercase">
              Signed in as
            </div>
            <div className="font-body text-sm text-[#FAFAFA] mt-0.5">
              {user?.username || "—"}
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-sm text-sm text-[#A1A1AA] hover:text-[#EF4444] hover:bg-[#EF4444]/10 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 flex items-center justify-between px-4 py-3 bg-[#0B0B0F] border-b border-[#27272A]">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-sm bg-[#10B981] flex items-center justify-center">
            <Pickaxe className="w-3.5 h-3.5 text-[#09090B]" strokeWidth={2.5} />
          </div>
          <div className="font-heading font-black tracking-tight">
            WART<span className="text-[#10B981]">HIVE</span>
          </div>
        </div>
        <button
          data-testid="logout-btn-mobile"
          onClick={handleLogout}
          className="text-[#A1A1AA] hover:text-[#EF4444]"
        >
          <LogOut className="w-4 h-4" />
        </button>
      </div>

      {/* Mobile bottom nav */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 grid grid-cols-6 bg-[#0B0B0F] border-t border-[#27272A]">
        {nav.map(({ to, label, Icon, end, testid }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={`${testid}-mobile`}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 py-2.5 text-[10px] font-mono uppercase tracking-wider ${
                isActive ? "text-[#10B981]" : "text-[#A1A1AA]"
              }`
            }
          >
            <Icon className="w-4 h-4" />
            <span className="truncate max-w-full px-1">{label.split(" ")[0]}</span>
          </NavLink>
        ))}
      </div>

      <main className="flex-1 pt-14 pb-20 lg:pt-0 lg:pb-0 min-w-0">{children}</main>
    </div>
  );
}
